import type { ShoppingListItem } from '@/types/shopping-list'

interface ParsedIngredient {
  item: string
  quantity?: number
  unit?: string
  originalText: string
  specification?: string
}

export class IngredientParser {
  // Common Dutch units and their variations
  private readonly units = new Map([
    // Volume
    ['ml', ['ml', 'milliliter', 'milliliters']],
    ['cl', ['cl', 'centiliter', 'centiliters']],
    ['dl', ['dl', 'deciliter', 'deciliters']],
    ['liter', ['l', 'liter', 'liters']],
    ['eetlepel', ['el', 'eetlepel', 'eetlepels', 'tbsp', 'tablespoon']],
    ['theelepel', ['tl', 'theelepel', 'theelepels', 'tsp', 'teaspoon']],
    ['kopje', ['kopje', 'kopjes', 'cup', 'cups']],
    
    // Weight
    ['gram', ['g', 'gr', 'gram', 'grams']],
    ['kilogram', ['kg', 'kilo', 'kilogram', 'kilograms']],
    
    // Count
    ['stuks', ['stuk', 'stuks', 'st', 'pieces', 'piece']],
    ['blikje', ['blik', 'blikje', 'blikjes', 'can', 'cans']],
    ['pakje', ['pak', 'pakje', 'pakjes', 'package', 'packages']],
    ['plak', ['plak', 'plakje', 'plakjes', 'slice', 'slices']],
    ['teen', ['teen', 'tenen', 'teentje', 'teentjes', 'clove', 'cloves']],
    ['bosje', ['bos', 'bosje', 'bosjes', 'bunch', 'bunches']],
    
    // Special Dutch measurements
    ['snufje', ['snuf', 'snufje', 'snufjes', 'pinch', 'pinches']],
    ['scheutje', ['scheut', 'scheutje', 'scheutjes', 'splash', 'splashes']],
    ['handje', ['hand', 'handje', 'handjes', 'handful', 'handfuls']],
  ])

  // Common specifications that should be preserved
  private readonly specifications = [
    'groot', 'grote', 'klein', 'kleine', 'middel', 'middelgroot', 'middelgrote',
    'vers', 'verse', 'gedroogd', 'gedroogde', 'fijngehakt', 'fijngesneden',
    'gesneden', 'gehakt', 'geraspt', 'geschild', 'ontpit', 'uitgelekt',
    'kamertemperatuur', 'koud', 'warm', 'lauw', 'gekookt', 'gebakken',
    'versgemalen', 'gesnipperd', 'gesnipperde', 'geperst', 'geperste',
    'biologisch', 'bio', 'extra', 'light', 'mager', 'halfvol', 'vol',
    'ongezouten', 'gezouten', 'ongewassen', 'gewassen', 'diepvries'
  ]

  // Words to remove from ingredient names
  private readonly stopWords = [
    'voor', 'van', 'in', 'op', 'aan', 'bij', 'met', 'zonder', 'extra',
    'eventueel', 'optioneel', 'naar', 'smaak', 'believen', 'serveren'
  ]

  parseIngredient(ingredientText: string): ParsedIngredient {
    const original = ingredientText.trim()
    let text = original.toLowerCase()

    // Drop a leading "optioneel:" / "voor de saus:" style label.
    text = text.replace(/^\s*(optioneel|eventueel|extra)\s*:\s*/i, '')

    // "appelazijn of citroensap" -> buy the first option.
    text = text.replace(/\s+of\s+.*$/i, '')

    // "pompoen, bijvoorbeeld flespompoen" -> drop the example.
    text = text.replace(/\s*\b(bijvoorbeeld|bijv\.?|zoals)\b.*$/i, '')

    // "scheutje appelazijn (voor frisheid)" -> drop the aside entirely.
    text = text.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim()

    // "kookvocht om de stamppot smeuïger te maken" -> drop the purpose.
    text = text.replace(/\s+om\s+.*?\bte\s+\w+.*$/i, '')

    // Extract the quantity first, then only treat the next word as a unit when
    // it really is one. Consuming it unconditionally swallowed the ingredient
    // itself for countable items like "2 rookworsten" or "3 eieren".
    // Handles "2", "1,5", "1-2" and fractions like "1/2" or "1 1/2".
    const quantityRegex =
      /^(\d+\s+\d+\s*\/\s*\d+|\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?(?:\s*[-–]\s*\d+(?:[.,]\d+)?)?)\s*/
    const match = text.match(quantityRegex)

    let quantity: number | undefined
    let unit: string | undefined
    let specification: string | undefined

    if (match) {
      quantity = this.parseQuantity(match[1])
      text = text.substring(match[0].length).trim()

      const unitMatch = text.match(/^([a-zA-Z]+)\.?\s*/)
      const candidateUnit = unitMatch?.[1]?.toLowerCase()
      if (candidateUnit && this.isKnownUnit(candidateUnit)) {
        unit = this.normalizeUnit(candidateUnit)
        text = text.substring(unitMatch![0].length).trim()
      }
    } else {
      // "scheutje olijfolie" has no number but still leads with a unit.
      const unitMatch = text.match(/^([a-zA-Z]+)\.?\s+/)
      const candidateUnit = unitMatch?.[1]?.toLowerCase()
      if (candidateUnit && this.isKnownUnit(candidateUnit)) {
        unit = this.normalizeUnit(candidateUnit)
        quantity = 1
        text = text.substring(unitMatch![0].length).trim()
      }
    }

    // Anything after a comma describes preparation ("2 uien, gesnipperd"), not
    // a different product, so keep it out of the item name.
    const commaIndex = text.indexOf(',')
    let preparation = ''
    if (commaIndex > 0) {
      preparation = text.substring(commaIndex + 1).trim()
      text = text.substring(0, commaIndex).trim()
    }

    // Extract specifications. Dutch adjectives take an -e/-en ending
    // ("warm" -> "warme"), so match and strip those too; otherwise the
    // adjective stays glued to the ingredient and derails product matching.
    // Some of these words double as ingredients ("500 g gehakt"), so never
    // strip one when nothing would be left.
    const specs: string[] = []
    for (const spec of this.specifications) {
      const specPattern = new RegExp(`\\b${spec}(e|en)?\\b`, 'g')
      if (!specPattern.test(text)) {
        continue
      }

      const stripped = text.replace(new RegExp(`\\b${spec}(e|en)?\\b`, 'g'), ' ').replace(/\s+/g, ' ').trim()
      if (!stripped) {
        continue
      }

      specs.push(spec)
      text = stripped
    }

    // Clean up the item name
    let item = text
      .replace(/[,;()]/g, ' ') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()

    // Remove stop words
    const words = item.split(' ')
    const cleanWords = words.filter(word => !this.stopWords.includes(word.toLowerCase()))
    item = cleanWords.join(' ')

    // Capitalize first letter
    item = item.charAt(0).toUpperCase() + item.slice(1)

    const allSpecs = [...specs, ...(preparation ? [preparation] : [])]
    if (allSpecs.length > 0) {
      specification = allSpecs.join(', ')
    }

    return {
      item,
      quantity,
      unit,
      originalText: original,
      specification
    }
  }

  /** Understands "1,5", a "1-2" range (takes the lower bound) and "1 1/2". */
  private parseQuantity(raw: string): number | undefined {
    const value = raw.replace(',', '.').replace(/\s*[-–]\s*\d+(?:\.\d+)?/, '').trim()

    const mixed = value.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/)
    if (mixed) {
      const whole = parseFloat(mixed[1])
      const denominator = parseFloat(mixed[3])
      return denominator ? whole + parseFloat(mixed[2]) / denominator : whole
    }

    const fraction = value.match(/^(\d+)\s*\/\s*(\d+)$/)
    if (fraction) {
      const denominator = parseFloat(fraction[2])
      return denominator ? parseFloat(fraction[1]) / denominator : undefined
    }

    const parsed = parseFloat(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  private isKnownUnit(unit: string): boolean {
    const lowerUnit = unit.toLowerCase()

    for (const variations of this.units.values()) {
      if (variations.includes(lowerUnit)) {
        return true
      }
    }

    return false
  }

  private normalizeUnit(unit: string): string {
    const lowerUnit = unit.toLowerCase()
    
    for (const [normalized, variations] of this.units) {
      if (variations.includes(lowerUnit)) {
        return normalized
      }
    }
    
    return unit // Return original if not found
  }

  parseIngredientsToShoppingList(ingredients: string[]): ShoppingListItem[] {
    const itemMap = new Map<string, ShoppingListItem>()

    for (const ingredient of ingredients) {
      const parsed = this.parseIngredient(ingredient)
      
      if (!parsed.item) continue

      const key = normalizeIngredientName(parsed.item)
      
      if (itemMap.has(key)) {
        // Combine quantities if same item appears multiple times
        const existing = itemMap.get(key)!
        if (existing.quantity && parsed.quantity && existing.unit === parsed.unit) {
          existing.quantity += parsed.quantity
        }
        // Add specification if different
        if (parsed.specification && !existing.specification?.includes(parsed.specification)) {
          existing.specification = existing.specification 
            ? `${existing.specification}, ${parsed.specification}`
            : parsed.specification
        }
      } else {
        itemMap.set(key, {
          name: parsed.item,
          quantity: parsed.quantity,
          unit: parsed.unit,
          specification: parsed.specification
        })
      }
    }

    return Array.from(itemMap.values())
  }

  formatShoppingListItem(item: ShoppingListItem): string {
    let formatted = item.name
    
    if (item.quantity && item.unit) {
      formatted = `${item.quantity} ${item.unit} ${formatted}`
    } else if (item.quantity) {
      formatted = `${item.quantity}x ${formatted}`
    }
    
    if (item.specification) {
      formatted += ` (${item.specification})`
    }
    
    return formatted
  }
}

const PLURAL_SUFFIXES = ['tjes', 'jes', 'tje', 'en', 's']

/**
 * Groups an ingredient with its singular/plural twin, so "1 ui" from one recipe
 * and "2 uien" from another end up on the shopping list once.
 */
export const normalizeIngredientName = (name: string): string => {
  const cleaned = name
    .toLowerCase()
    .replace(/[.,;()]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  const words = cleaned.split(' ')
  const last = words[words.length - 1] ?? ''

  for (const suffix of PLURAL_SUFFIXES) {
    if (last.endsWith(suffix) && last.length - suffix.length >= 2) {
      words[words.length - 1] = last.slice(0, -suffix.length)
      return words.join(' ')
    }
  }

  return cleaned
}

// Export singleton instance
export const ingredientParser = new IngredientParser()
export type { ParsedIngredient, ShoppingListItem }
